import { useState, useEffect } from "react";
import { Upload, X, Loader2, Image as ImageIcon } from "lucide-react";
import api, { inDemo } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

const isUrl = (v) => typeof v === "string" && /^https?:\/\//i.test(v);

// Fetches a private image as an authenticated blob and returns an object URL.
export function useAuthedImage(value) {
  const [src, setSrc] = useState("");
  useEffect(() => {
    if (!value) { setSrc(""); return; }
    if (isUrl(value)) { setSrc(value); return; }
    let active = true;
    let objUrl;
    (async () => {
      try {
        const { data } = await api.get(`/files/${value}`, { responseType: "blob" });
        if (!active) return;
        objUrl = URL.createObjectURL(data);
        setSrc(objUrl);
      } catch { if (active) setSrc(""); }
    })();
    return () => { active = false; if (objUrl) URL.revokeObjectURL(objUrl); };
  }, [value]);
  return src;
}

export function ImageThumb({ value }) {
  const src = useAuthedImage(value);
  if (!src) return <span className="text-muted-foreground">—</span>;
  return <img src={src} alt="" className="h-10 w-14 object-cover rounded-md border border-border" />;
}

export function ImageField({ value, onChange, testId = "image" }) {
  const preview = useAuthedImage(value);
  const [busy, setBusy] = useState(false);

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (inDemo()) { window.dispatchEvent(new CustomEvent("demo-locked")); return; }
    if (!file.type.startsWith("image/")) { toast.error("Please choose an image file."); return; }
    if (file.size > 10 * 1024 * 1024) { toast.error("Image is too large (max 10MB)."); return; }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/uploads", fd, { headers: { "Content-Type": "multipart/form-data" } });
      onChange(data.path);
      toast.success("Image uploaded");
    } catch (err) {
      if (!err?.__demo_locked) toast.error(err?.response?.data?.detail || "Upload failed. Please try again.");
    } finally { setBusy(false); }
  };

  return (
    <div>
      <label className="block cursor-pointer">
        <input type="file" accept="image/jpeg,image/png,image/webp,image/gif" onChange={onFile} className="hidden" data-testid={`${testId}-input`} />
        {preview ? (
          <div className="relative group rounded-xl overflow-hidden border border-border" data-testid={`${testId}-preview`}>
            <img src={preview} alt="" className="w-full h-40 object-cover" />
            <div className="absolute inset-0 bg-black/30 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
              <span className="text-white text-xs font-medium flex items-center gap-1.5"><Upload className="h-4 w-4" /> Replace</span>
            </div>
          </div>
        ) : (
          <div className="w-full h-32 rounded-xl border-2 border-dashed border-border flex flex-col items-center justify-center gap-2 text-muted-foreground hover:border-accent hover:text-accent transition-colors" data-testid={`${testId}-dropzone`}>
            {busy ? <Loader2 className="h-5 w-5 animate-spin" /> : <ImageIcon className="h-5 w-5" />}
            <span className="text-xs">{busy ? "Uploading…" : "Click to upload an image"}</span>
            <span className="text-[10px] text-muted-foreground/70">JPEG, PNG, WEBP or GIF · max 10MB</span>
          </div>
        )}
      </label>
      {value && (
        <button type="button" onClick={() => onChange("")} className="mt-2 inline-flex items-center gap-1 text-xs text-destructive hover:underline" data-testid={`${testId}-remove`}>
          <X className="h-3 w-3" /> Remove image
        </button>
      )}
    </div>
  );
}
